/**
 * Bi-directional sync helpers — SED_Amendment_Persistence SA6.
 *
 * Two pure-ish helpers that propagate writes across the SED ↔ Site
 * Context boundary. Since CBE-A3 the binding rows are sourced from the
 * CBE `CONTEXT_BINDING_REGISTRY` (via `sedSyncAdapter`) rather than the
 * legacy `SED_BINDING_REGISTRY` — same surface, same behaviour, pinned
 * by the legacy-equivalence oracle suite
 * (`__tests__/syncSedContextBindings.oracle.test.ts`):
 *
 *   - syncSedAmendmentToContext  — when an amendment lands on the SED,
 *     mirror its `newValue` onto every claim bound to that path.
 *   - syncContextClaimToSed      — when a claim's `claimedValue` changes,
 *     emit an SEDAmendment seeded against each bound SED path. Forward
 *     coercion runs through the CBE resolver with `trustValue` (the
 *     claim's value is already typed — a user-EDITED value need not
 *     appear in the original sourceSpan).
 *
 * Both helpers are **deepEqual-guarded at the binding boundary**: if
 * `reverseProjection(coerce(claimValue))` already equals the SED value
 * (or the symmetric check on the other side), the sync is a no-op. This
 * is the load-bearing invariant that prevents an infinite ping-pong when
 * both sides write back into each other through the registry.
 *
 * Engineering rules (CLAUDE.md §1, §3):
 *   - The deepEqual guard is the convergence proof. SA8 includes a
 *     regression that deletes the guard and demonstrates a single-step
 *     livelock detector (not a timeout). `bindingDeepEqual` keeps its
 *     1e-9 relative tolerance — deliberately NOT routed through the
 *     merge layer's `valuesAgree` (1e-6), which would loosen the
 *     convergence proof.
 *   - Helpers return the proposed mutation as a plain object. The
 *     caller is responsible for batching them into a Firestore
 *     transaction (per the spec's "single transaction" rule).
 *   - Audit events are emitted via the soft-fail client. They never
 *     block the sync result.
 */
import type { ExtractedClaim, SiteContext, SyncHistoryEntry } from '../../types/siteContext';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SEDAmendment, AmendmentSource } from '../../types/sedAmendments';
import { emitAuditEvent } from '../../lib/auditLogClient';
import { resolveSedField } from './crossCheckClaims';
import { bindingSedPathToVariablePath } from './sedBindingRegistry';
import { claimsToFacts } from '../../lib/contextFinancialFill';
import { filledResolutions, resolveContextBindings } from '../contextIngestion/resolveContextBindings';
import {
  cbeValueToSyncScalar,
  isSedSyncRow,
  reverseProjectionFor,
  sedSyncGroups,
  sedSyncRowsForTag,
  syncBindingForVariablePath,
} from '../contextIngestion/sedSyncAdapter';

/**
 * Structural deep equality for binding-boundary values. Bound values
 * are scalars (number / string / boolean) or `undefined` / `null`. We
 * intentionally do NOT extend this to arrays or objects — bindings that
 * carry composite values are out of scope for SA6 and would invalidate
 * the convergence proof (deep comparison of nested objects through
 * lossy projections is not generally a fixed point).
 */
function bindingDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Both nullish ⇒ equal (treats null and undefined as the same
  // "no value" state at the binding boundary).
  if (a == null && b == null) return true;
  // NaN equality — Number.isNaN both sides.
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    // Tolerance for float-derived projections (km from m, etc.).
    if (Number.isFinite(a) && Number.isFinite(b)) {
      const denom = Math.max(Math.abs(a), Math.abs(b), 1);
      return Math.abs(a - b) / denom < 1e-9;
    }
  }
  return false;
}

/* ── Forward sync: SED amendment → Site Context claims ────────────── */

export interface SyncSedToContextResult {
  /** The updated SiteContext to persist, or null if no-op. */
  updated: SiteContext | null;
  /** Number of claims whose value was rewritten. */
  claimsUpdated: number;
  /** Number of binding-boundary writes that the deepEqual guard skipped. */
  skippedNoOp: number;
}

/**
 * When an SEDAmendment lands (e.g. user edits a SED variable in the
 * variable browser), mirror its `newValue` onto every site-context
 * claim bound to that path.
 *
 * Caller passes the SEDAmendment AND the current `SiteContext`. The
 * helper returns the new SiteContext (or `null` if nothing changed).
 *
 * NOTE: the helper does NOT write to Firestore. The caller batches the
 * returned object into its own transaction alongside the amendment
 * write — keeping the binding write and the amendment write atomic.
 */
export function syncSedAmendmentToContext(args: {
  amendment: Pick<SEDAmendment, 'variablePath' | 'newValue' | 'createdAt' | 'createdBy' | 'createdByEmail' | 'syncedToSiteContextClaimId'>;
  siteContext: SiteContext;
  /** When true, suppress audit emissions (used by tests). */
  suppressAudit?: boolean;
}): SyncSedToContextResult {
  const { amendment, siteContext, suppressAudit } = args;
  // Path → winning CBE sync row (mirrors legacy `getBindingForSedPath`:
  // exact path first, then the variable-path normalisation). Paths whose
  // winner is not sync-eligible (e.g. locusKeyword-only targets) resolve
  // to undefined — the legacy registry never bound them.
  const binding = syncBindingForVariablePath(amendment.variablePath);
  const project = binding ? reverseProjectionFor(binding) : undefined;
  if (!binding || !project) {
    return { updated: null, claimsUpdated: 0, skippedNoOp: 0 };
  }

  // Project the SED value onto the claim domain ONCE, up-front.
  const projected = project(amendment.newValue);

  let claimsUpdated = 0;
  let skippedNoOp = 0;
  const updatedClaims: ExtractedClaim[] = [];

  for (const claim of siteContext.claims) {
    // Only fan out to claims in the matching category — AND skip the
    // originating claim (when the amendment was itself synthesised from
    // a claim-write via the reverse helper).
    const fires = sedSyncRowsForTag(claim.category).some(
      b => b === binding,
    );
    if (!fires) {
      updatedClaims.push(claim);
      continue;
    }
    if (
      amendment.syncedToSiteContextClaimId &&
      amendment.syncedToSiteContextClaimId === claim.id
    ) {
      // This amendment was itself a side-effect of a write on THIS
      // claim. Echoing it back is exactly the livelock the deepEqual
      // guard exists to prevent — short-circuit explicitly.
      updatedClaims.push(claim);
      continue;
    }

    // Convergence check: is the claim's current value already equal to
    // the projected SED value? If so, this is a no-op write.
    if (bindingDeepEqual(claim.claimedValue, projected)) {
      skippedNoOp += 1;
      updatedClaims.push(claim);
      continue;
    }

    const next: SyncHistoryEntry = {
      sedPath: amendment.variablePath,
      resolution: 'auto',
      resolvedAt: amendment.createdAt,
      resolvedBy: amendment.createdBy
        ? { uid: amendment.createdBy, email: amendment.createdByEmail }
        : undefined,
    };
    updatedClaims.push({
      ...claim,
      claimedValue: projected,
      lastWrittenAt: amendment.createdAt,
      lastWrittenBy: amendment.createdBy
        ? { uid: amendment.createdBy, email: amendment.createdByEmail }
        : undefined,
      syncHistory: [...(claim.syncHistory ?? []), next],
    });
    claimsUpdated += 1;

    if (!suppressAudit) {
      void emitAuditEvent({
        action: 'sed_context_sync',
        target: { type: 'site', id: 'context', label: claim.id },
        before: { claimedValue: claim.claimedValue },
        after: { claimedValue: projected },
        metadata: {
          direction: 'sed→context',
          claimId: claim.id,
          claimCategory: claim.category,
          sedPath: amendment.variablePath,
        },
      });
    }
  }

  if (claimsUpdated === 0) {
    return { updated: null, claimsUpdated: 0, skippedNoOp };
  }
  const updated: SiteContext = {
    ...siteContext,
    claims: updatedClaims,
    updatedAt: amendment.createdAt,
    updatedBy: amendment.createdBy
      ? { uid: amendment.createdBy, email: amendment.createdByEmail }
      : siteContext.updatedBy,
  };
  return { updated, claimsUpdated, skippedNoOp };
}

/* ── Reverse sync: Site Context claim → SED amendment(s) ─────────── */

export interface SyncContextToSedResult {
  /** New SEDAmendments to enqueue, one per bound SED path. */
  amendments: SEDAmendment[];
  /** Binding-boundary writes that the deepEqual guard skipped. */
  skippedNoOp: number;
}

/**
 * When a Site Context claim's `claimedValue` changes (e.g. user edits
 * a row in the diff tab and "Adopt Site Context value"), emit a fresh
 * SEDAmendment seeded against each SED path the claim is bound to.
 *
 * Caller provides the current SED so we can read the prior SED value
 * (becomes the amendment's `oldValue`). The helper does NOT mutate the
 * SED — it returns drafted amendments and the caller batches them into
 * the same transaction as the SiteContext write.
 */
export function syncContextClaimToSed(args: {
  claim: ExtractedClaim;
  sed: SiteEvidenceDocument | null;
  sedId: string;
  sedVersion: number;
  actor: { uid: string; email: string };
  /** When true, suppress audit emissions (used by tests). */
  suppressAudit?: boolean;
  /** Optional override of "now" — tests pin to a deterministic value. */
  now?: () => string;
}): SyncContextToSedResult {
  const { claim, sed, sedId, sedVersion, actor, suppressAudit } = args;
  const nowFn = args.now ?? (() => new Date().toISOString());

  if (sedSyncRowsForTag(claim.category).length === 0) {
    return { amendments: [], skippedNoOp: 0 };
  }

  // Forward coercion through the CBE resolver (`trustValue`: the claim's
  // `claimedValue` is an already-typed trusted value — a user-EDITED value
  // legitimately no longer appears in the original sourceSpan, so the
  // extraction-time value-in-span guard must not reject it). Resolutions
  // are then filtered to the path-keyed SYNC rows — intent / financial
  // surfaces and span-conditional rows are other callers' business.
  const resolutions = filledResolutions(
    resolveContextBindings(claimsToFacts([claim]), { trustValue: true }),
  );

  const amendments: SEDAmendment[] = [];
  let skippedNoOp = 0;
  for (const resolution of resolutions) {
    const binding = resolution.binding;
    if (!isSedSyncRow(binding)) continue;
    if (resolution.value === undefined) continue; // filledResolutions guarantees, but guard
    const projected = cbeValueToSyncScalar(resolution.value);
    if (projected === undefined) continue;

    const sedPath = resolution.targetField.path;
    const sedCurrent = sed ? resolveSedField(sed, sedPath) : null;

    // Convergence check at the binding boundary. If the SED already
    // holds an equal value, drafting another amendment would loop.
    if (bindingDeepEqual(sedCurrent, projected)) {
      skippedNoOp += 1;
      continue;
    }

    const createdAt = nowFn();
    const id = `sync-${claim.id}-${sedPath.replace(/\./g, '_')}-${createdAt}`;
    const source: AmendmentSource = {
      type: 'extracted-context',
      label: 'Auto-synced from site context',
    };
    const variableLabel = binding.label;

    amendments.push({
      id,
      sedId,
      sedVersion,
      variablePath: bindingSedPathToVariablePath(sedPath),
      variableLabel,
      domain: sedPath.split('.')[0] ?? 'unknown',
      oldValue: (sedCurrent ?? null) as SEDAmendment['oldValue'],
      newValue: projected as SEDAmendment['newValue'],
      reason: `Auto-propagated from Site Context claim "${claim.claim}"`,
      source,
      createdBy: actor.uid,
      createdByEmail: actor.email,
      createdAt,
      status: 'pending',
      syncedToSiteContextClaimId: claim.id,
    });

    if (!suppressAudit) {
      void emitAuditEvent({
        action: 'sed_context_sync',
        target: { type: 'site', id: sedId, label: sedPath },
        before: { sedValue: sedCurrent ?? null },
        after: { sedValue: projected },
        metadata: {
          direction: 'context→sed',
          claimId: claim.id,
          claimCategory: claim.category,
          sedPath,
        },
      });
    }
  }

  return { amendments, skippedNoOp };
}

/* ── Diff rows (read model for the SA7 diff tab) ─────────────────── */

export type DiffRowStatus = 'in-sync' | 'diverged' | 'sed-only' | 'context-only';

export interface SedContextDiffRow {
  /** Stable identity for UD preservation: `${claimId}::${sedPath}`. */
  identity: string;
  /** Claim id (or `null` when the SED side has no matching claim). */
  claimId: string | null;
  /** The claim category (or null when the SED side has no claim). */
  claimCategory: ExtractedClaim['category'] | null;
  /** SED path the row is keyed on. */
  sedPath: string;
  /** Human-readable label for the row header. */
  label: string;
  /** Current SED value (resolved, projected to the claim domain). */
  sedValue: ExtractedClaim['claimedValue'] | null;
  /** Current Site Context value. */
  contextValue: ExtractedClaim['claimedValue'] | null;
  /** Computed state for the chip / colour. */
  status: DiffRowStatus;
  /** The latest user resolution (if any), preserved across rebuild. */
  resolution?: SyncHistoryEntry['resolution'];
  /** When `resolution` is set — ISO timestamp of that resolution. */
  resolvedAt?: string;
  /** Who resolved it. */
  resolvedBy?: { uid: string; email?: string };
  /** When multiple context claims map to the same binding, how many were collapsed. */
  collapsedClaimCount?: number;
}

/**
 * SCR-1 — "resolved ≠ open divergence".
 *
 * `status` on a diff row is a PURE value-signal: `bindingDeepEqual(ctx,
 * sedVal) ? 'in-sync' : 'diverged'`. But "Adopt SED" / "Adopt Context"
 * queues a *pending* amendment (`syncContextClaimToSed` returns
 * `status:'pending'`) — the opposite side's value does not change until a
 * later rebuild applies it, so the values still differ and `status` stays
 * `'diverged'` even though the user has already decided the row.
 *
 * `isRowResolved` is the single shared definition of "a user has settled
 * this row" so the rule can't drift across the diff tab and the landing
 * page's promotion count. A row carries a resolution iff `claim.syncHistory`
 * recorded a non-`auto` resolution (`'adopt-sed'` / `'adopt-context'`);
 * `'auto'` is a bi-directional sync echo, not a user decision, so it never
 * counts as "resolved" here.
 */
export function isRowResolved(row: Pick<SedContextDiffRow, 'resolution'>): boolean {
  return !!row.resolution && row.resolution !== 'auto';
}

/** Status as displayed: a still-value-diverged row that the user has
 * resolved reads as `'resolved'` (a settled state), NOT `'diverged'`. A
 * row whose values genuinely converged stays `'in-sync'`. */
export type EffectiveDiffStatus = DiffRowStatus | 'resolved';

export function effectiveDiffStatus(
  row: Pick<SedContextDiffRow, 'status' | 'resolution'>,
): EffectiveDiffStatus {
  if (row.status === 'diverged' && isRowResolved(row)) return 'resolved';
  return row.status;
}

/** True only for an OPEN (unresolved) divergence — the rows that still
 * need a user decision and that surface as red badges / promotion counts. */
export function isOpenDivergence(
  row: Pick<SedContextDiffRow, 'status' | 'resolution'>,
): boolean {
  return row.status === 'diverged' && !isRowResolved(row);
}

function pickPrimaryClaimForPath(
  claims: ExtractedClaim[],
  sedPath: string,
): ExtractedClaim {
  const withResolution = claims
    .filter(c => (c.syncHistory ?? []).some(h => h.sedPath === sedPath))
    .sort((a, b) => {
      const ha = (a.syncHistory ?? []).filter(h => h.sedPath === sedPath).at(-1)?.resolvedAt ?? '';
      const hb = (b.syncHistory ?? []).filter(h => h.sedPath === sedPath).at(-1)?.resolvedAt ?? '';
      return hb.localeCompare(ha);
    });
  if (withResolution.length > 0) return withResolution[0]!;

  return claims.slice().sort((a, b) =>
    (Date.parse(b.lastWrittenAt ?? '') || 0) - (Date.parse(a.lastWrittenAt ?? '') || 0),
  )[0]!;
}

/**
 * Build the side-by-side diff rows for every bound claim/SED path pair.
 * Pure — no I/O. Caller passes the *currently* rendered SiteContext +
 * SED; the row's resolution overlay is applied later by the UI through
 * `mergePreservedUserDecisions` against the prior render.
 */
export function buildSedContextDiffRows(args: {
  siteContext: SiteContext | null | undefined;
  sed: SiteEvidenceDocument | null;
}): SedContextDiffRow[] {
  const { siteContext, sed } = args;
  const rows: SedContextDiffRow[] = [];

  // Index claims by category for O(1) lookup per binding. SCE-3 —
  // claims the user flagged as `state: 'mis-extracted'` are filtered
  // out at this boundary so the diff tab never renders them; the row
  // itself stays in `siteContext.claims[]` for audit + the Restore
  // affordance in the SED Variable Browser.
  const claimsByCategory = new Map<ExtractedClaim['category'], ExtractedClaim[]>();
  for (const c of siteContext?.claims ?? []) {
    if (c.state === 'mis-extracted') continue;
    const list = claimsByCategory.get(c.category) ?? [];
    list.push(c);
    claimsByCategory.set(c.category, list);
  }

  // Iterate the CBE sync rows grouped per tag — the same shape as the
  // legacy registry's per-entry iteration (`technology.reactor` groups
  // its two path rows; rows keep the legacy `sedPaths` order).
  for (const group of sedSyncGroups()) {
    const claims = claimsByCategory.get(group.tag) ?? [];
    const firstRow = group.rows[0];
    if (!firstRow) continue; // groups are non-empty by construction
    const sedFirstPath = firstRow.targetField.path;
    const sedRaw = sed ? resolveSedField(sed, sedFirstPath) : null;
    const projectFirst = reverseProjectionFor(firstRow);
    const sedProjected = projectFirst ? projectFirst(sedRaw) : (sedRaw as ExtractedClaim['claimedValue']);

    if (claims.length === 0) {
      // Bound SED path with no matching claim. Surface as `sed-only` so
      // the user can see the SED has a value the context doesn't claim.
      if (sedProjected !== undefined && sedProjected !== null) {
        rows.push({
          identity: `__nocontext__::${sedFirstPath}`,
          claimId: null,
          claimCategory: null,
          sedPath: sedFirstPath,
          label: firstRow.label,
          sedValue: sedProjected,
          contextValue: null,
          status: 'sed-only',
        });
      }
      continue;
    }

    for (const row of group.rows) {
      const sedPath = row.targetField.path;
      const claim = pickPrimaryClaimForPath(claims, sedPath);
      const thisRaw = sed ? resolveSedField(sed, sedPath) : null;
      const project = reverseProjectionFor(row);
      const thisProjected = project ? project(thisRaw) : (thisRaw as ExtractedClaim['claimedValue']);
      const ctx = claim.claimedValue ?? null;
      const sedVal = thisProjected ?? null;

      let status: DiffRowStatus;
      if (ctx === null && sedVal === null) continue;
      if (ctx === null) status = 'sed-only';
      else if (sedVal === null) status = 'context-only';
      else if (bindingDeepEqual(ctx, sedVal)) status = 'in-sync';
      else status = 'diverged';

      const histForPath = (claim.syncHistory ?? []).filter(h => h.sedPath === sedPath);
      const latest = histForPath.length > 0 ? histForPath[histForPath.length - 1] : undefined;

      rows.push({
        identity: `${claim.id}::${sedPath}`,
        claimId: claim.id,
        claimCategory: claim.category,
        sedPath,
        label: row.label,
        sedValue: sedVal,
        contextValue: ctx,
        status,
        resolution: latest?.resolution,
        resolvedAt: latest?.resolvedAt,
        resolvedBy: latest?.resolvedBy,
        collapsedClaimCount: claims.length > 1 ? claims.length : undefined,
      });
    }
  }

  return rows;
}

/* ── Re-export bindingDeepEqual for the regression suite ──────────── */
export const __TEST_bindingDeepEqual = bindingDeepEqual;
